import { type FC, memo } from "react";
import { isNil } from "lodash-es";
import { css } from "@stubhub/broadway-foundation/css";
import type { PerformerSeoBottomContentModel } from "src/http/api-types";
import { DataTestIds } from "src/shared/constants/TestEnums";
import { LeagueTeamsTable } from "../league-teams-table/LeagueTeamsTable";
import { PerformerEventTable } from "../performer-event-table/PerformerEventTable";
import { SportsScheduleTable } from "../sports-schedule-table/SportsScheduleTable";
import { SeoTableSkeleton } from "./SeoTableSkeleton";

// SeoTable owns no vertical rhythm — stack the two sports tables with the same gap the eager/SSR layout applies.
const sportsTablesStack = css({ display: "flex", flexDirection: "column", rowGap: "primitives.200" });

interface Props {
  eventTableRows?: PerformerSeoBottomContentModel["performerEventTableRows"];
  sportsScheduleRows?: PerformerSeoBottomContentModel["performerSportsScheduleTableRows"];
  leagueTeamsRows?: PerformerSeoBottomContentModel["leagueTeamsTableRows"];
  performerName: string;
  isMajorLeagueSportsTeam: boolean;
  isPending: boolean;
}

/**
 * Presentational only — the caller owns the data (model rows on the bot/SSR path, the
 * proximity-triggered fetch on the variant path) and decides when to mount this.
 */
export const PerformerSeoTables: FC<Props> = memo(
  ({ eventTableRows, sportsScheduleRows, leagueTeamsRows, performerName, isMajorLeagueSportsTeam, isPending }) => {
    if (isPending && isNil(eventTableRows) && isNil(sportsScheduleRows) && isNil(leagueTeamsRows)) {
      return (
        <div data-testid={DataTestIds.PerformerSeoEventTable}>
          <SeoTableSkeleton />
        </div>
      );
    }

    if (isMajorLeagueSportsTeam) {
      if (!sportsScheduleRows?.length && !leagueTeamsRows?.length) {
        return null;
      }
      return (
        <div className={sportsTablesStack} data-testid={DataTestIds.PerformerSeoEventTable}>
          {/* League teams before the schedule — single source of the render order. */}
          {!!leagueTeamsRows?.length && <LeagueTeamsTable rows={leagueTeamsRows} leagueName={performerName} />}
          {!!sportsScheduleRows?.length && (
            <SportsScheduleTable rows={sportsScheduleRows} performerName={performerName} />
          )}
        </div>
      );
    }

    if (!eventTableRows?.length) {
      return null;
    }

    return (
      <div data-testid={DataTestIds.PerformerSeoEventTable}>
        <PerformerEventTable rows={eventTableRows} performerName={performerName} />
      </div>
    );
  },
);

PerformerSeoTables.displayName = "PerformerSeoTables";
